/**
 * Pure ordering helpers for a field catalog that is composed from several
 * lists (Signals V3 PR 1, U1). A leaf module on purpose: `filter-fields.ts`
 * (the catalog sink) imports it, and so can `signal-view.ts`, without either
 * direction creating a runtime import cycle (see signal-view.ts's header).
 */

/**
 * Stable regroup: entries keep their relative order, but every entry lands
 * inside the run of the group it FIRST appeared under, and groups keep their
 * first-appearance order. Guarantees each group heading prints exactly once
 * in a sectioned picker, however many lists the catalog was stitched from.
 */
export function groupContiguous<T extends { group: string }>(entries: readonly T[]): T[] {
  const order: string[] = [];
  const byGroup = new Map<string, T[]>();
  for (const entry of entries) {
    let bucket = byGroup.get(entry.group);
    if (!bucket) {
      bucket = [];
      byGroup.set(entry.group, bucket);
      order.push(entry.group);
    }
    bucket.push(entry);
  }
  return order.flatMap(group => byGroup.get(group)!);
}

/**
 * Entries whose key has a label in `labels` take that label; everything else
 * is returned untouched. Lets the Filter, Group and Sort pickers (which read
 * different lists) show one name per field.
 */
export function unifyLabels<T extends { key: string; label: string }>(
  entries: readonly T[],
  labels: ReadonlyMap<string, string>,
): T[] {
  return entries.map(entry => {
    const label = labels.get(entry.key);
    return label === undefined || label === entry.label ? entry : { ...entry, label };
  });
}

/**
 * Longest example line a picker row is meant to show (characters, before the
 * trailing "…"). The picker is ~280px wide in a 12px muted font; this keeps the
 * second line on one row. Applies to the whole line, "e.g. " included; enforced by a catalog test.
 */
export const FIELD_EXAMPLES_MAX_CHARS = 40;

const MAX_EXAMPLE_VALUES = 3;

const EXAMPLES_PREFIX = 'e.g. ';

/**
 * The muted second line under a field's name in every field picker: ONLY
 * example values, e.g. "e.g. Call, Manual email, Auto email".
 *
 * A field with an enumerated `values` list derives it (display labels, first
 * three, "…" when the list was cut) so the line can never drift from the real
 * options; otherwise the hand-written `hint` (already in "e.g. …" form);
 * otherwise nothing. Values are added only while the whole line — prefix and
 * ellipsis included — stays within `FIELD_EXAMPLES_MAX_CHARS` (the first is
 * always kept), so one long value cannot overflow the row.
 */
export function fieldExamples(entry: {
  values?: ReadonlyArray<string | { value: string; label: string }>;
  hint?: string;
}): string | undefined {
  const labels = (entry.values ?? []).map(option => (typeof option === 'string' ? option : option.label));
  if (labels.length === 0) return entry.hint;
  const lineOf = (shown: readonly string[]) => `${EXAMPLES_PREFIX}${shown.join(', ')}${shown.length < labels.length ? '…' : ''}`;
  const shown: string[] = [];
  for (const label of labels.slice(0, MAX_EXAMPLE_VALUES)) {
    if (shown.length > 0 && lineOf([...shown, label]).length > FIELD_EXAMPLES_MAX_CHARS) break;
    shown.push(label);
  }
  return lineOf(shown);
}
